import cloneDeep from "lodash/cloneDeep";
import { Dispatch } from "redux";
import { datadogLogs } from '@datadog/browser-logs';
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { initialMessagingTemplatePerType } from "@constants/initial-messaging-template-per-type";
import {
    MESSAGING_TEMPLATE_TYPE_TO_TEMPLATE_FIELD_NAME_MAP,
    MESSAGING_TEMPLATE_TYPE_TO_LANGUAGE_FIELD_NAME_MAP,
    EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_SUBJECT_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_SUBJECT_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_HEADER_LOGO_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_LANGUAGE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_TEMPLATE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_DCUI_SMS_LANGUAGE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_DCUI_SMS_TEMPLATE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_LANGUAGE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_TEMPLATE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_PIN_SMS_LANGUAGE_FILE_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_PIN_SMS_TEMPLATE_FILE_PROPERTY_NAME
} from "@constants/messaging-template-constants";
import {
    MESSAGING_TEMPLATE_DCUI_EMAIL_DEFAULT,
    MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME,
    MESSAGING_TEMPLATE_DCUI_SMS_DEFAULT,
    MESSAGING_TEMPLATE_HEADER_LOGO_VARIABLE_NAME,
    MESSAGING_TEMPLATE_PIN_EMAIL_DEFAULT,
    MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME,
    MESSAGING_TEMPLATE_PIN_SMS_DEFAULT
} from "@constants/messaging-template-default";
import {
    ACUANT_DRIVERS_LICENSE_VERIFICATION_FRONT_AND_BACK_SERVICE_OFFERING_ID,
    ACUANT_DRIVERS_LICENSE_VERIFICATION_FRONT_BACK_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
    ACUANT_FACE_MATCH_SERVICE_OFFERING_ID,
    ACUANT_ID_CARD_VERIFICATION_FRONT_BACK_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
    ACUANT_ID_CARD_VERIFICATION_FRONT_BACK_SERVICE_OFFERING_ID,
    ACUANT_PASSIVE_LIVENESS_SERVICE_OFFERING_ID,
    ACUANT_PASSPORT_VERIFICATION_FRONT_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
    ACUANT_PASSPORT_VERIFICATION_FRONT_SERVICE_OFFERING_ID,
    EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID,
    INCODE_BIOGOVID_DUAL_DOCUMENT_VERIFICATION_SERVICE_OFFERING_ID,
    INCODE_BIOGOVID_VERIFICATION_DEEPFAKE_SERVICE_OFFERING_ID,
    INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
    ONFIDO_DRIVERS_LICENSE_VERIFICATION_SERVICE_OFFERING_ID,
    ONFIDO_PASSPORT_VERIFICATION_SERVICE_OFFERING_ID,
    SELFIE_DATABASE_MATCH_INCODE_SERVICE_OFFERING_ID,
    SELFIE_DATABASE_MATCH_PLUS_DEEPFAKE_DETECTION_INCODE_SERVICE_OFFERING_ID,
    TRUSTSTAMP_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
    BIOGOVID_VERIFICATION_COMBO_SERVICE_OFFERING_ID
} from "@constants/verification-service-details-constants";
import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import {
    FormattedMessagingTemplate,
    MessagingTemplateConfiguration,
    MessagingTemplate,
    MessagingTemplateCache,
    MessagingTemplateCacheObject
} from "@idw-types/data/messaging-template";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { getMessagingTemplateRequest } from "@requests/axnadmin-core";
import { formatMessagingTemplate } from "@formatters/messaging-templates/messaging-template-formatter";
import { createMessagingTemplateRequest } from "@requests/axnadmin-core/create-messaging-template";
import { isFulfilled, isRejected } from "@utils/promise-util";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { hasEmailPinServiceOffering, hasSmsPinServiceOffering } from "@utils/pin-code/pin-code-utils";
import { IDWSession } from "@idw-types/data/idw-session";
import { isAxnAdminUser } from "@utils/permissions-util";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { SelectOption } from "@idw-types/common";
import { updateMessagingTemplateRequest } from "@requests/axnadmin-core/update-messaging-templates";
import { getInheritedAndMergedJSONPropertyValue } from "@utils/verification-service-details-util";
import { isTmxInDCUIEnabled } from "@utils/dcui-capture-step-util";

export const getTemplateFileFieldNameForTemplateType = (templateType: MessagingTemplateTypes) => {
    return MESSAGING_TEMPLATE_TYPE_TO_TEMPLATE_FIELD_NAME_MAP[templateType];
};

export const getLanguageFileFieldNameForTemplateType = (templateType: MessagingTemplateTypes) => {
    return MESSAGING_TEMPLATE_TYPE_TO_LANGUAGE_FIELD_NAME_MAP[templateType];
};

export const hasDcuiSmsAndDcuiEmailServiceOffering = (serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>) => {
    return serviceOfferings
        .some(so => so.addedToService && so.status === EntityStatusTypes.ACTIVE && [
            INCODE_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
            INCODE_BIOGOVID_DUAL_DOCUMENT_VERIFICATION_SERVICE_OFFERING_ID,
            SELFIE_DATABASE_MATCH_INCODE_SERVICE_OFFERING_ID,
            SELFIE_DATABASE_MATCH_PLUS_DEEPFAKE_DETECTION_INCODE_SERVICE_OFFERING_ID,
            INCODE_BIOGOVID_VERIFICATION_DEEPFAKE_SERVICE_OFFERING_ID,
            TRUSTSTAMP_BIOGOVID_VERIFICATION_SERVICE_OFFERING_ID,
            ACUANT_PASSPORT_VERIFICATION_FRONT_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
            ACUANT_DRIVERS_LICENSE_VERIFICATION_FRONT_AND_BACK_SERVICE_OFFERING_ID,
            ACUANT_DRIVERS_LICENSE_VERIFICATION_FRONT_BACK_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
            ACUANT_ID_CARD_VERIFICATION_FRONT_BACK_AND_FACE_COMPARISON_SERVICE_OFFERING_ID,
            ACUANT_ID_CARD_VERIFICATION_FRONT_BACK_SERVICE_OFFERING_ID,
            ACUANT_PASSPORT_VERIFICATION_FRONT_SERVICE_OFFERING_ID,
            ONFIDO_DRIVERS_LICENSE_VERIFICATION_SERVICE_OFFERING_ID,
            ONFIDO_PASSPORT_VERIFICATION_SERVICE_OFFERING_ID,
            ACUANT_PASSIVE_LIVENESS_SERVICE_OFFERING_ID,
            ACUANT_FACE_MATCH_SERVICE_OFFERING_ID,
            BIOGOVID_VERIFICATION_COMBO_SERVICE_OFFERING_ID
        ].includes(so.serviceOfferingID));
};

export const createCopyOfMessagingTemplate = (
    messagingTemplate: MessagingTemplate,
    relyingPartyID: string,
    companyID: string
): MessagingTemplate => {

    if (messagingTemplate.companyID !== null) {
        return messagingTemplate;
    } else {
        return {
            ...messagingTemplate,
            id: null,
            uuid: null,
            name: `${messagingTemplate.name}-copy`,
            companyID,
            relyingPartyID
        }
    }
};

export const getAvailableMessagingTemplateTypes = (
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    tmxInDCUIEnabled: boolean
) => {
    const availableMessagingTemplateTypes: Array<MessagingTemplateTypes> = [];

    if (hasEmailPinServiceOffering(serviceOfferings)) {
        availableMessagingTemplateTypes.push(MessagingTemplateTypes.PIN_EMAIL);
    }
    if (hasSmsPinServiceOffering(serviceOfferings)) {
        availableMessagingTemplateTypes.push(MessagingTemplateTypes.PIN_SMS);
    }
    if (hasDcuiSmsAndDcuiEmailServiceOffering(serviceOfferings) || tmxInDCUIEnabled) {
        availableMessagingTemplateTypes.push(MessagingTemplateTypes.DCUI_EMAIL, MessagingTemplateTypes.DCUI_SMS);
    }

    return availableMessagingTemplateTypes;
};

export const addMessagingTemplateOption = (messagingTemplate: MessagingTemplateConfiguration) => {
    return {
        label: messagingTemplate.name,
        value: messagingTemplate.id
    }
}

export const getMessagingTemplatePerTypeOptions = (messagingTemplates: Array<MessagingTemplateConfiguration>) => {
    return messagingTemplates.reduce((result, messagingTemplate) => {
        const { type } = messagingTemplate;

        if (type === MessagingTemplateTypes.PIN_EMAIL) {
            result[MessagingTemplateTypes.PIN_EMAIL].push(addMessagingTemplateOption(messagingTemplate));
        }

        if (type === MessagingTemplateTypes.DCUI_EMAIL) {
            result[MessagingTemplateTypes.DCUI_EMAIL].push(addMessagingTemplateOption(messagingTemplate));
        }

        if (type === MessagingTemplateTypes.PIN_SMS) {
            result[MessagingTemplateTypes.PIN_SMS].push(addMessagingTemplateOption(messagingTemplate));
        }

        if (type === MessagingTemplateTypes.DCUI_SMS) {
            result[MessagingTemplateTypes.DCUI_SMS].push(addMessagingTemplateOption(messagingTemplate));
        }

        return result;
    }, cloneDeep(initialMessagingTemplatePerType));
};

export const getEmailTemplateFieldsJSONValueFromMessagingTemplates = (messagingTemplates: Array<FormattedMessagingTemplate>) => {
    return messagingTemplates.reduce((emailTemplateFields, messagingTemplate) => {
        const templateFileName = getTemplateFileFieldNameForTemplateType(messagingTemplate.type);
        const languageTemplateFileName = getLanguageFileFieldNameForTemplateType(messagingTemplate.type);

        emailTemplateFields[templateFileName] = messagingTemplate.temporaryUUID ? `${messagingTemplate.temporaryUUID}.ftl` : null;
        emailTemplateFields[languageTemplateFileName] = messagingTemplate.temporaryUUID ? `${messagingTemplate.temporaryUUID}.json` : null;

        if (messagingTemplate.type === MessagingTemplateTypes.PIN_EMAIL) {
            emailTemplateFields[EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_SUBJECT_PROPERTY_NAME] = `\${(${MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME})!""}`;
            emailTemplateFields[EMAIL_TEMPLATE_FIELDS_HEADER_LOGO_PROPERTY_NAME] = `\${(${MESSAGING_TEMPLATE_HEADER_LOGO_VARIABLE_NAME})!""}`
        } else if (messagingTemplate.type === MessagingTemplateTypes.DCUI_EMAIL) {
            emailTemplateFields[EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_SUBJECT_PROPERTY_NAME] = `\${(${MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME})!""}`;
        }

        return emailTemplateFields;
    }, {} as EmailTemplateFields);
};

export const getMessagingTemplateDetails = (dispatch: Dispatch) => async (
    id: string,
    uuid: string,
    messagingTemplateCache: MessagingTemplateCache,
    serviceRelyingParty: AxnRelyingParty,
    axnCompanies: Array<AxnCompany>,
    axnRelyingParties: Array<AxnRelyingParty>
): Promise<MessagingTemplateCacheObject> => {
    if (messagingTemplateCache[uuid]) {
        return messagingTemplateCache[uuid];
    } else {
        const { data, error } = await getMessagingTemplateRequest(id);

        if (data) {
            const newMessagingTemplate = createCopyOfMessagingTemplate(data, serviceRelyingParty?.id, serviceRelyingParty?.companyID);
            const formattedMessagingTemplate = formatMessagingTemplate(newMessagingTemplate, axnCompanies, axnRelyingParties);

            return {
                messagingTemplate: data,
                formattedMessagingTemplate
            };
        } else {
            if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.getMessagingTemplateDetails.error.title,
                    resources.requests.getMessagingTemplateDetails.error.message,
                    null
                );
            }
        }
    }

    return null;
};

export const getMessagingTemplateDetailsConfigurations = (dispatch: Dispatch) => async (
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>,
    messagingTemplateConfigurations: Array<MessagingTemplateConfiguration>,
    emailTemplateFields: EmailTemplateFields,
    messagingTemplateCache: MessagingTemplateCache,
    serviceRelyingParty: AxnRelyingParty,
    axnCompanies: Array<AxnCompany>,
    axnRelyingParties: Array<AxnRelyingParty>
) => {

    const requests: Array<Promise<MessagingTemplateCacheObject>> = availableMessagingTemplateTypes.reduce((requestsArray, templateType) => {
        const fileName = getTemplateFileFieldNameForTemplateType(templateType);

        if (emailTemplateFields[fileName]) {
            const templateUuid = emailTemplateFields[fileName].split(".")[0];
            const templateConfiguration = messagingTemplateConfigurations.find(template => template.uuid === templateUuid);

            if (templateConfiguration) {
                const { id, uuid } = templateConfiguration;
                requestsArray.push(getMessagingTemplateDetails(dispatch)(id, uuid, messagingTemplateCache, serviceRelyingParty, axnCompanies, axnRelyingParties));
            }
        }
        return requestsArray;
    }, [] as Array<Promise<MessagingTemplateCacheObject>>);

    const responses = await Promise.allSettled(requests);

    return responses.reduce((newMessagingTemplateCache, response) => {
        if (isFulfilled(response) && response.value?.formattedMessagingTemplate) {
            const messagingTemplateUUID = response.value.formattedMessagingTemplate.temporaryUUID;
            newMessagingTemplateCache[messagingTemplateUUID] = response.value;
        }
        return newMessagingTemplateCache;
    }, {} as MessagingTemplateCache);
};

export const updateOrCreateMessagingTemplates = (dispatch: Dispatch) => async (messagingTemplates: Array<FormattedMessagingTemplate>): Promise<Array<MessagingTemplate>> => {
    const requests = messagingTemplates.map(messagingTemplate => {
        const { template, name, company, relyingParty, type, languageFiles, id, status } = messagingTemplate;

        const languages = getUnformattedLanguageFiles(languageFiles);

        if (id === null) {
            return createMessagingTemplateRequest(template, name, company?.id, relyingParty?.id, type, languages);
        } else {
            return updateMessagingTemplateRequest(
                id,
                template,
                name,
                company?.id,
                relyingParty?.id,
                type,
                getUnformattedLanguageFiles(languageFiles),
                status,
                messagingTemplate.uuid,
                messagingTemplate.permissions
            );
        }
    });

    const responses = await Promise.allSettled(requests);

    let shouldShowError = false;
    let detailedErrorMessage = "";

    const newMessagingTemplates: Array<MessagingTemplate> = responses.reduce((newMessagingTemplatesArray, response) => {
        if (isFulfilled(response)) {
            const { data, error, body } = response.value;

            if (data && body) {
                newMessagingTemplatesArray.push({ permissions: body.permissions, ...data });
            } else if (error) {
                shouldShowError = true;
                if (!detailedErrorMessage) {
                    detailedErrorMessage = error;
                    datadogLogs.logger.error(resources.requests.updateMessagingTemplate.error.title, error);
                }
            }
        } else if (isRejected(response)) {
            shouldShowError = true;
            if (!detailedErrorMessage) {
                detailedErrorMessage = response.reason;
            }
            datadogLogs.logger.error(resources.requests.updateMessagingTemplate.error.title, response.reason);
        }

        return newMessagingTemplatesArray;
    }, [] as Array<MessagingTemplate>);

    if (shouldShowError) {
        logAndDisplayErrorModal(
            dispatch,
            detailedErrorMessage,
            resources.requests.updateMessagingTemplate.error.title,
            resources.requests.updateMessagingTemplate.error.message
        );
    }

    return newMessagingTemplates;
};

export const getEmailTemplateFieldsAndMessagingTemplateCacheUpdates = (dispatch: Dispatch) => async (
    selectedMessagingTemplates: Array<FormattedMessagingTemplate>,
    axnCompanies: Array<AxnCompany>,
    axnRelyingParties: Array<AxnRelyingParty>,
) => {

    //get new messaging templates data
    const newMessagingTemplates = await updateOrCreateMessagingTemplates(dispatch)(selectedMessagingTemplates);

    const newMessagingTemplateCache = newMessagingTemplates.reduce((newMessagingTemplateCache, messagingTemplate) => {
        const formattedMessagingTemplate = formatMessagingTemplate(messagingTemplate, axnCompanies, axnRelyingParties);
        newMessagingTemplateCache[formattedMessagingTemplate.temporaryUUID] = {
            messagingTemplate: messagingTemplate,
            formattedMessagingTemplate: formattedMessagingTemplate
        }
        return newMessagingTemplateCache;
    }, {} as MessagingTemplateCache);

    const newFormattedMessagingTemplates = Object.keys(newMessagingTemplateCache).map(templateUUID => newMessagingTemplateCache[templateUUID].formattedMessagingTemplate);

    //extract email template fields from new data
    const newEmailTemplateFields = getEmailTemplateFieldsJSONValueFromMessagingTemplates(newFormattedMessagingTemplates);

    return {
        newEmailTemplateFields,
        newMessagingTemplateCache
    };
};

export const getCleanedEmailTemplateFields = (emailTemplateFields: EmailTemplateFields) => {
    const cleanedEmailTemplateFields: EmailTemplateFields = {
        ...emailTemplateFields,
        [EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_LANGUAGE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_TEMPLATE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_DCUI_SMS_LANGUAGE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_DCUI_SMS_TEMPLATE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_LANGUAGE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_TEMPLATE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_PIN_SMS_LANGUAGE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_PIN_SMS_TEMPLATE_FILE_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_PIN_EMAIL_SUBJECT_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_HEADER_LOGO_PROPERTY_NAME]: undefined,
        [EMAIL_TEMPLATE_FIELDS_DCUI_EMAIL_SUBJECT_PROPERTY_NAME]: undefined
    };
    return cleanedEmailTemplateFields;
};

const getDefaultTemplateAvailabilityLevel = (session: IDWSession, isVericationServiceDetailsPage: boolean) => {
    const isRpUser = session.user.axnAdminUserRPProperties.length > 0;
    const isAdminUser = isAxnAdminUser(session.user);
    const isOrgUser = !isAdminUser && !isRpUser;
    if (isAdminUser) {
        return isVericationServiceDetailsPage ? TemplateAvailabilityLevelTypes.ORGANIZATION : TemplateAvailabilityLevelTypes.GLOBAL;
    } else if (isOrgUser) {
        return TemplateAvailabilityLevelTypes.ORGANIZATION;
    } else {
        return TemplateAvailabilityLevelTypes.RELYING_PARTY;
    }
};

export const getTemplateAndLanguageFiles = (defaultTemplateType: MessagingTemplateTypes): { content: string; languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any; }>> } => {
    let content = null;
    let languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any; }>> = { [LocaleTypes.ENGLISH]: {} };

    switch (defaultTemplateType) {
        case MessagingTemplateTypes.PIN_EMAIL: {
            content = MESSAGING_TEMPLATE_PIN_EMAIL_DEFAULT;
            languageFiles = { [LocaleTypes.ENGLISH]: { emailpin: { subject: "Verification Code" } } };
            break;
        }
        case MessagingTemplateTypes.PIN_SMS: {
            content = MESSAGING_TEMPLATE_PIN_SMS_DEFAULT;
            break;
        }
        case MessagingTemplateTypes.DCUI_EMAIL: {
            content = MESSAGING_TEMPLATE_DCUI_EMAIL_DEFAULT;
            languageFiles = { [LocaleTypes.ENGLISH]: { dcuiEmail: { subject: "Verification Link" } } };
            break;
        }
        default: {
            content = MESSAGING_TEMPLATE_DCUI_SMS_DEFAULT;
            break;
        }
    }

    return {
        content,
        languageFiles
    }
}

export const getInitialMessagingTemplate = (
    axnCompanies: Array<AxnCompany>, axnRelyingParties: AxnRelyingParty[], isVericationServiceDetailsPage: boolean,
    session: IDWSession, availableMessagingTemplateTypeOptions: Array<SelectOption<MessagingTemplateTypes>>
): FormattedMessagingTemplate => {
    const defaultTemplateType = availableMessagingTemplateTypeOptions[0].value;
    const { content, languageFiles } = getTemplateAndLanguageFiles(defaultTemplateType);

    const initialMessagingTemplate: FormattedMessagingTemplate = {
        template: content,
        name: "",
        type: defaultTemplateType,
        company: null,
        relyingParty: null,
        id: null,
        uuid: null,
        temporaryUUID: null,
        status: EntityStatusTypes.ACTIVE,
        languageFiles,
        templateAvailabilityLevel: null,
        permissions: {
            canModifyBranding: true,
            canModify: true,
            canViewBranding: true
        }
    };

    let company: AxnCompany = null;
    let relyingParty: AxnRelyingParty = null;
    const templateAvailabilityLevel = getDefaultTemplateAvailabilityLevel(session, isVericationServiceDetailsPage);

    if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION) {
        company = axnCompanies[0];
    } else if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.RELYING_PARTY) {
        company = axnCompanies[0];
        relyingParty = axnRelyingParties.find(rp => rp.companyID === company.id) || null;
    }

    return {
        ...initialMessagingTemplate,
        templateAvailabilityLevel,
        company,
        relyingParty
    }
}

export const getUnformattedLanguageFiles = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>): Partial<MultiLanguageWrapper<string>> => {
    return (Object.keys(languageFiles || {}) as Array<LocaleTypes>).reduce((result, locale) => {
        const localeString = JSON.stringify(languageFiles[locale]) || "";
        result[locale] = localeString;
        return result;

    }, {} as Partial<MultiLanguageWrapper<string>>);
};

export const getSelectedMessagingTemplates = (
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>,
    mergedEmailTemplateFields: EmailTemplateFields,
    messagingTemplateCache: MessagingTemplateCache
) => {
    return availableMessagingTemplateTypes.reduce((newSelectedMessagingTemplates, templateType) => {
        const templateFileFieldName = getTemplateFileFieldNameForTemplateType(templateType);
        const languageFileFieldName = getLanguageFileFieldNameForTemplateType(templateType);

        //if the property is set up to use a template for this template type
        if (
            mergedEmailTemplateFields[templateFileFieldName] &&
            mergedEmailTemplateFields[languageFileFieldName]
        ) {
            //check the cache and store the template in the selected list if found
            const uuid = mergedEmailTemplateFields[templateFileFieldName].split(".")[0];
            if (messagingTemplateCache[uuid]) {
                newSelectedMessagingTemplates.push(messagingTemplateCache[uuid].formattedMessagingTemplate);
            }
        }
        return newSelectedMessagingTemplates;
    }, [] as Array<FormattedMessagingTemplate>);
};

export const getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig = (rpServiceConfig: FormattedRpServiceConfiguration) => {
    const tmxInDCUIEnabled = isTmxInDCUIEnabled(rpServiceConfig.properties);
    const availableMessagingTemplateTypes = getAvailableMessagingTemplateTypes(rpServiceConfig.serviceOfferings, tmxInDCUIEnabled);
    const {
        mergedValue: mergedEmailTemplateFields,
        inheritedValue: inheritedEmailTemplateFields
    } = getInheritedAndMergedJSONPropertyValue(
        rpServiceConfig.properties,
        rpServiceConfig.appliedProfiles,
        EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID,
        {} as EmailTemplateFields
    );
    return {
        availableMessagingTemplateTypes,
        mergedEmailTemplateFields,
        inheritedEmailTemplateFields
    };
};